import React from "react";

/**
 * Shared admin table: plain columns via `columns`; optional fixed layout + col widths.
 *
 * @typedef {{
 *   id: string,
 *   header: React.ReactNode,
 *   width?: string,
 *   thClassName?: string,
 *   tdClassName?: string | ((row: object) => string | undefined),
 *   align?: 'left' | 'center' | 'right',
 *   render: (row: object, rowIndex: number) => React.ReactNode,
 * }} AdminDataTableColumn
 *
 * @param {{
 *   layout?: 'default' | 'fixed',
 *   columns: AdminDataTableColumn[],
 *   rows: object[],
 *   getRowKey?: (row: object) => string | number,
 *   wrapClassName?: string,
 *   tableClassName?: string,
 *   emptyState?: React.ReactNode,
 * }} props
 */
export default function AdminDataTable({
  layout = "default",
  columns,
  rows,
  getRowKey = (row) => row.id,
  wrapClassName = "",
  tableClassName = "",
  emptyState,
}) {
  const fixed = layout === "fixed";
  const wrapCls = ["adm-table-wrap", fixed ? "adm-table-wrap--data" : "", wrapClassName].filter(Boolean).join(" ");
  const tblCls = ["adm-table", fixed ? "adm-table--data" : "", tableClassName].filter(Boolean).join(" ");

  const cellClass = (col, row) => {
    if (!col.tdClassName) return undefined;
    return typeof col.tdClassName === "function" ? col.tdClassName(row) : col.tdClassName;
  };

  const alignStyle = (col) => {
    if (col.align === "center") return { textAlign: "center" };
    if (col.align === "right") return { textAlign: "right" };
    return undefined;
  };

  return (
    <div className={wrapCls}>
      <table className={tblCls}>
        {fixed ? (
          <colgroup>
            {columns.map((c) => (
              <col key={c.id} style={c.width ? { width: c.width } : undefined} />
            ))}
          </colgroup>
        ) : null}
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.id} scope="col" className={c.thClassName} style={alignStyle(c)}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="adm-table-empty-cell">
                {emptyState != null ? emptyState : "\u00a0"}
              </td>
            </tr>
          ) : (
            rows.map((row, rowIndex) => (
              <tr key={String(getRowKey(row))}>
                {columns.map((c) => (
                  <td key={c.id} className={cellClass(c, row)} style={alignStyle(c)}>
                    {c.render(row, rowIndex)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
